import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

// Client for the urdu-realtime-1 API (urduvoice/platform/CONTRACT.md). The session lives in an
// HttpOnly cookie on ai-api.neu.so, so every request goes with credentials: "include".
export const API_URL = (process.env.GATSBY_API_URL || "https://ai-api.neu.so").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message || code);
    this.status = status; // 0 = network failure
    this.code = code;
  }
}

async function request(method, path, body) {
  let res;
  try {
    res = await fetch(API_URL + path, {
      method,
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, "network", "Could not reach the server. Check your connection and try again.");
  }
  if (res.status === 204) return null;
  let data = null;
  try { data = await res.json(); } catch { /* empty or non-JSON body */ }
  if (!res.ok) {
    const code = (data && data.error) || `http_${res.status}`;
    throw new ApiError(res.status, code, errorMessage(code, res.status));
  }
  return data;
}

const MESSAGES = {
  network: "Could not reach the server. Check your connection and try again.",
  invalid_email: "Enter a valid email address.",
  weak_password: "Use at least 8 characters for your password.",
  email_taken: "An account with this email already exists. Log in instead.",
  invalid_credentials: "Email or password is wrong.",
  unauthorized: "Please log in again.",
  payments_not_enabled: "Payments open soon. During the beta every call is free.",
  rate_limited: "Too many attempts. Wait a minute and try again.",
  call_active: "You already have a call open. Hang up there first.",
};

export function errorMessage(code, status) {
  if (MESSAGES[code]) return MESSAGES[code];
  if (status === 429) return MESSAGES.rate_limited;
  if (status >= 500) return "Something went wrong on our side. Try again in a minute.";
  return "Something went wrong. Try again.";
}

// ---- Endpoints (CONTRACT.md sections 1-3) ----
export const api = {
  signup: ({ email, password, name }) => request("POST", "/v1/auth/signup", { email, password, ...(name ? { name } : {}) }),
  login: ({ email, password }) => request("POST", "/v1/auth/login", { email, password }),
  logout: () => request("POST", "/v1/auth/logout"),
  me: () => request("GET", "/v1/me"),
  credits: () => request("GET", "/v1/credits"),
  checkout: packId => request("POST", "/v1/credits/checkout", { pack_id: packId }),
  startCall: () => request("POST", "/v1/calls"),
  calls: () => request("GET", "/v1/calls"),
};

// ---- Session: GET /v1/me once per page load, shared through context ----
// status: "loading" | "in" | "out" | "error"
const SessionContext = createContext({ status: "loading", me: null, refresh: async () => {}, setMe: () => {} });

export function SessionProvider({ children }) {
  const [state, setState] = useState({ status: "loading", me: null, error: null });

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setState({ status: "in", me, error: null });
      return me;
    } catch (e) {
      if (e.status === 401) setState({ status: "out", me: null, error: null });
      else setState({ status: "error", me: null, error: e });
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signOut = useCallback(async () => {
    try { await api.logout(); } catch { /* cookie may already be gone */ }
    setState({ status: "out", me: null, error: null });
  }, []);

  return (
    <SessionContext.Provider value={{ ...state, refresh, signOut }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);

// ---- Formatting ----
export const dollars = cents => `$${((cents || 0) / 100).toFixed(2)}`;
export const formatDate = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};
export const formatLength = sec => {
  const n = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

// Only allow same-site relative redirects from ?next=
export function nextPath(search, fallback = "/models") {
  try {
    const n = new URLSearchParams(search).get("next");
    if (n && n.startsWith("/") && !n.startsWith("//")) return n;
  } catch { /* ignore */ }
  return fallback;
}
